import { useEffect, useState } from "react";
import { getVersion } from "./services/api";

// ─── Seeded credentials ───────────────────────────────────────────────────────
const CREDENTIALS = {
  user: {
    username: "user",
    password: "user123",
    role: "user",
    displayName: "User",
  },
  admin: {
    username: "admin123",
    password: "P@5sw0rd",
    role: "admin",
    displayName: "Admin",
  },
};

// ─── Nav items & role access ──────────────────────────────────────────────────
const ALL_NAV_ITEMS = [
  { id: "analytics",          label: "Analytics",   header: "Analytics [EX-TAX]",  icon: "📊", roles: ["user", "admin"] },
  { id: "commissions",        label: "Commissions", header: "Commissions",          icon: "💰", roles: ["user", "admin"] },
  { id: "products",           label: "Products",    header: "Products",             icon: "📦", roles: ["user", "admin"] },
  { id: "report-settings",    label: "Reports",     header: "Report Settings",      icon: "📧", roles: ["admin"] },
  { id: "messaging-settings", label: "Messaging",   header: "Messaging Settings",   icon: "💬", roles: ["admin"] },
];

// ─── Login Page ───────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [isAdmin, setIsAdmin]       = useState(false);
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");
  const [showPass, setShowPass]     = useState(false);
  const [shaking, setShaking]       = useState(false);

  // Reset fields when toggling role
  const handleToggle = (adminMode) => {
    setIsAdmin(adminMode);
    setUsername("");
    setPassword("");
    setError("");
    setShowPass(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const target = isAdmin ? CREDENTIALS.admin : CREDENTIALS.user;

    if (username === target.username && password === target.password) {
      onLogin({ role: target.role, displayName: target.displayName });
    } else {
      setError("Invalid username or password.");
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">

      {/* Decorative blobs */}
      <div className="absolute top-[-80px] left-[-80px] w-96 h-96 bg-cyan-500 opacity-10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-80px] right-[-80px] w-96 h-96 bg-blue-600 opacity-10 rounded-full blur-3xl pointer-events-none" />

      <div
        className={`relative z-10 w-full max-w-sm mx-4 ${shaking ? "animate-shake" : ""}`}
        style={shaking ? { animation: "shake 0.4s ease" } : {}}
      >
        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">

          {/* Logo / brand */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-8 py-6 text-center">
            <h1 className="text-2xl font-bold text-white tracking-tight">ShortcutsPOS</h1>
            <p className="text-cyan-100 text-sm mt-1 opacity-80">Point of Sale Dashboard</p>
          </div>

          <div className="px-8 py-8">

            {/* Role toggle */}
            <div className="flex rounded-xl overflow-hidden border border-white/10 mb-7 text-sm font-semibold">
              <button
                type="button"
                onClick={() => handleToggle(false)}
                className={`flex-1 py-2.5 transition-all duration-200 ${
                  !isAdmin
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-inner"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                👤 User
              </button>
              <button
                type="button"
                onClick={() => handleToggle(true)}
                className={`flex-1 py-2.5 transition-all duration-200 ${
                  isAdmin
                    ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-inner"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                🛡️ Admin
              </button>
            </div>

            {/* Role badge */}
            <p className="text-center text-xs mb-5 font-medium tracking-wide uppercase">
              <span
                className={`px-3 py-1 rounded-full ${
                  isAdmin
                    ? "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                    : "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                }`}
              >
                Signing in as {isAdmin ? "Administrator" : "User"}
              </span>
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
                  Username
                </label>
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setError(""); }}
                  placeholder={isAdmin ? "admin123" : "user"}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-slate-600 rounded-lg px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-slate-600 rounded-lg px-4 py-3 pr-10 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition text-base"
                    tabIndex={-1}
                  >
                    {showPass ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <p className="text-red-400 text-xs text-center bg-red-500/10 border border-red-500/20 rounded-lg py-2 px-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className={`w-full mt-1 py-3 rounded-lg font-semibold text-sm text-white shadow-lg transition-all duration-200 active:scale-95 ${
                  isAdmin
                    ? "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-violet-500/20"
                    : "bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-cyan-500/20"
                }`}
              >
                Sign In
              </button>
            </form>
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs mt-4">ShortcutsPOS © 2025</p>
      </div>

      {/* Shake keyframe injected inline */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-6px); }
          80%       { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}

// ─── Main Layout ──────────────────────────────────────────────────────────────
export default function Layout({ activePage, setActivePage, children }) {
  const [session, setSession]   = useState(null); // null = not logged in
  const [version, setVersion]   = useState("Loading...");

  useEffect(() => {
    async function fetchVersion() {
      const v = await getVersion();
      setVersion(v);
    }
    fetchVersion();
  }, []);

  // Show login if not authenticated
  if (!session) {
    return <LoginPage onLogin={(s) => setSession(s)} />;
  }

  // Filter nav items by role
  const navItems = ALL_NAV_ITEMS.filter((item) => item.roles.includes(session.role));

  // If current activePage is not allowed for this role, reset to first allowed
  const allowedIds = navItems.map((i) => i.id);
  const safePage   = allowedIds.includes(activePage) ? activePage : allowedIds[0];
  if (safePage !== activePage) setActivePage(safePage);

  const activeHeader = navItems.find((item) => item.id === activePage)?.header;
  const isAdmin      = session.role === "admin";

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-xl flex flex-col justify-between border-r border-slate-200">
        <div>
          <div
            className={`h-16 flex items-center px-6 border-b border-slate-200 ${
              isAdmin
                ? "bg-gradient-to-r from-violet-600 to-indigo-600"
                : "bg-gradient-to-r from-cyan-500 to-blue-500"
            }`}
          >
            <h1 className="text-xl font-bold text-white tracking-tight">ShortcutsPOS</h1>
          </div>

          {/* Role badge in sidebar */}
          <div className="px-4 pt-4 pb-1">
            <div
              className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full w-fit ${
                isAdmin
                  ? "bg-violet-100 text-violet-700"
                  : "bg-cyan-100 text-cyan-700"
              }`}
            >
              <span>{isAdmin ? "🛡️" : "👤"}</span>
              <span>{session.displayName}</span>
            </div>
          </div>

          <nav className="mt-3 px-3">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => setActivePage(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      activePage === item.id
                        ? isAdmin
                          ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md transform scale-105"
                          : "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md transform scale-105"
                        : "text-slate-700 hover:bg-slate-100 hover:translate-x-1"
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="px-4 py-4 border-t border-slate-200 space-y-3">
          {/* Logout */}
          <button
            onClick={() => setSession(null)}
            className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-slate-500 hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <span>🚪</span>
            <span className="font-medium">Sign Out</span>
          </button>
          <div className="text-xs text-slate-400">Backend v{version}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white flex items-center px-6 shadow-sm border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div
              className={`h-8 w-1 rounded-full ${
                isAdmin
                  ? "bg-gradient-to-b from-violet-600 to-indigo-600"
                  : "bg-gradient-to-b from-cyan-500 to-blue-500"
              }`}
            />
            <h2
              className={`text-xl font-semibold bg-clip-text text-transparent capitalize ${
                isAdmin
                  ? "bg-gradient-to-r from-violet-600 to-indigo-600"
                  : "bg-gradient-to-r from-cyan-600 to-blue-600"
              }`}
            >
              {activeHeader}
            </h2>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}